'use client';

import { useState, useEffect } from 'react';
import { useRouter } from 'next/navigation';
import { PlusCircle, Loader2 } from 'lucide-react';
import { useContent } from '@/contexts/ContentContext';

const STATUS_TABS = [
  { label: 'All', value: '' },
  { label: 'Drafts', value: 'draft' },
  { label: 'Pending', value: 'pending_review' },
  { label: 'Published', value: 'posted' },
];

const PLATFORM_EMOJI: Record<string, string> = {
  linkedin: '💼', instagram: '📸', youtube: '🎬', tiktok: '🎵', x: '𝕏',
};

const STATUS_COLORS: Record<string, string> = {
  draft: 'bg-gray-500/20 text-gray-400',
  pending_review: 'bg-amber-500/20 text-amber-400',
  posted: 'bg-green-500/20 text-green-400',
  published: 'bg-green-500/20 text-green-400',
  approved: 'bg-cyan-500/20 text-cyan-400',
};

export default function ContentLibraryPage() {
  const router = useRouter();
  const { posts, postsLoading, refreshPosts } = useContent();
  const [filter, setFilter] = useState('');

  useEffect(() => { refreshPosts(); }, [refreshPosts]);

  const filtered = filter ? posts.filter(p => p.status === filter) : posts;

  if (postsLoading) {
    return (
      <div className="flex justify-center py-20">
        <Loader2 className="w-6 h-6 animate-spin text-cyan-400" />
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-5xl">
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-semibold text-white">Content Library</h1>
        <button onClick={() => router.push('/content/create')}
          className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-medium bg-cyan-500 text-black hover:bg-cyan-400">
          <PlusCircle className="w-4 h-4" /> Create New
        </button>
      </div>

      <div className="flex gap-1 bg-white/[0.05] rounded-lg p-1 w-fit">
        {STATUS_TABS.map(tab => (
          <button key={tab.value} onClick={() => setFilter(tab.value)}
            className={`px-4 py-1.5 rounded-md text-sm font-medium transition-all ${
              filter === tab.value ? 'bg-cyan-500/20 text-cyan-400' : 'text-gray-400 hover:text-white'
            }`}>
            {tab.label}
          </button>
        ))}
      </div>

      {filtered.length === 0 ? (
        <div className="text-center py-20">
          <p className="text-gray-500 mb-4">{filter ? 'No posts with this status' : 'No content yet'}</p>
          <button onClick={() => router.push('/content/create')}
            className="flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm bg-cyan-500/10 text-cyan-400 border border-cyan-500/20 mx-auto">
            <PlusCircle className="w-4 h-4" /> Create your first post
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filtered.map(post => (
            <div key={post.id} onClick={() => router.push(`/content/${post.id}`)}
              className="glass-card p-4 cursor-pointer hover:border-gray-700 transition-all">
              <div className="flex items-start justify-between mb-2">
                <span className="text-lg">{post.content_type === 'video' ? '🎬' : '📸'}</span>
                <div className="flex items-center gap-2">
                  {post.platform && <span className="text-xs">{PLATFORM_EMOJI[post.platform] || post.platform}</span>}
                  <span className={`text-[10px] px-2 py-0.5 rounded-full ${STATUS_COLORS[post.status] || 'bg-gray-800 text-gray-400'}`}>
                    {post.status.replace(/_/g, ' ')}
                  </span>
                </div>
              </div>
              <p className="text-sm text-white font-medium line-clamp-2 mb-1">{post.topic || 'Untitled'}</p>
              {post.hook && <p className="text-xs text-gray-400 line-clamp-1">{post.hook}</p>}
              {post.created_at && <p className="text-xs text-gray-600 mt-2">{new Date(post.created_at * 1000).toLocaleDateString()}</p>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
